import type {
  DatabaseBlockModel,
  DatabaseBlockProps,
} from '@blocksuite/affine-model';
import {
  createDatabaseReferenceProps,
  createDatabaseShardManifest,
  createShardedDatabaseIdentityProps,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { isSerializedXYWH } from '@blocksuite/global/gfx';
import { type BlockModel, type Store, Text } from '@blocksuite/store';

import { createCanvasSummaryPresentationState } from './canvas-presentation-state.js';
import type { DatabaseBlockDataSource } from './data-source.js';
import { getSystemWorkspaceStore } from './data-source-storage.js';
import { textFromString as titleTextFromString } from './properties/title/define.js';

export type ShardedDatabaseRootCreationInput = {
  databaseId?: string;
  title?: string;
};

export type ShardedDatabaseReferenceProps = ReturnType<
  typeof createDatabaseReferenceProps
> & { id: string };

export type ShardedDatabaseHostBlockProvisionInput = {
  databaseId?: string;
  title: string;
  parentId?: string;
  props?: Record<string, unknown>;
  addBlock?: (
    flavour: 'affine:database',
    props: Record<string, unknown>,
    parentId: string
  ) => string | null | undefined;
};

export type ShardedDatabaseHostBlockProvisionResult = {
  databaseBlockId: string;
  referenceProps: ShardedDatabaseReferenceProps;
  model: DatabaseBlockModel;
  storageModel: DatabaseBlockModel;
};

export type ShardedDatabaseBlockDataSourceProvisionInput =
  ShardedDatabaseHostBlockProvisionInput & {
    initDataSource?: (dataSource: DatabaseBlockDataSource) => void;
  };

export type ShardedDatabaseBlockDataSourceProvisionResult =
  ShardedDatabaseHostBlockProvisionResult & {
    dataSource: DatabaseBlockDataSource;
  };

export function normalizeDatabaseShardManifestOrNull(value: unknown) {
  try {
    return normalizeDatabaseShardManifest(value) ?? null;
  } catch {
    return null;
  }
}

export function selectChangedModelProps(
  model: DatabaseBlockModel,
  props: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(props).filter(([key, value]) => {
      const current = model.props[key as keyof DatabaseBlockProps];
      if (current && value && key === 'title') {
        return current.toString() !== value.toString();
      }
      if (Object.is(current, value)) return false;
      try {
        return JSON.stringify(current) !== JSON.stringify(value);
      } catch {
        return true;
      }
    })
  );
}

export function updateModelPropsIfChanged(
  model: DatabaseBlockModel,
  props: Record<string, unknown>
): void {
  const changedProps = selectChangedModelProps(model, props);
  if (Object.keys(changedProps).length === 0) return;
  model.store.withoutTransact(() => {
    model.store.updateBlock(model, changedProps);
    Object.assign(model.props, changedProps);
  });
}

export function ensureShardedDatabaseRootModelProps(
  model: DatabaseBlockModel,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title'> = {}
): void {
  const databaseId = referenceProps.databaseId;
  const databaseRootDocId = referenceProps.databaseRootDocId;
  if (!databaseId || !databaseRootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database root model repair requires databaseId and databaseRootDocId.'
    );
  }

  const manifest = normalizeDatabaseShardManifestOrNull(
    model.props.databaseShardManifest
  );
  const manifestMatchesReference =
    manifest?.databaseId === databaseId &&
    manifest.databaseRootDocId === databaseRootDocId &&
    manifest.workspaceId === model.store.workspace.id;
  const nextManifest = manifestMatchesReference
    ? manifest
    : createDatabaseShardManifest({
        databaseId,
        databaseRootDocId,
        workspaceId: model.store.workspace.id,
        shards: [],
      });

  const props = {
    databaseId,
    databaseRootDocId,
    databaseStorageVersion: referenceProps.databaseStorageVersion,
    databaseShardManifest: nextManifest,
    ...(model.props.title?.toString()
      ? {}
      : { title: titleTextFromString(input.title ?? 'Untitled Database') }),
  };
  updateModelPropsIfChanged(model, props);
}

export function ensureShardedDatabaseHostModelProps(
  model: DatabaseBlockModel,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title'> = {}
): void {
  const databaseId = referenceProps.databaseId;
  const databaseRootDocId = referenceProps.databaseRootDocId;
  if (!databaseId || !databaseRootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database host model repair requires databaseId and databaseRootDocId.'
    );
  }
  if (model.flavour !== 'affine:database') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${model.id}" is not a database block.`
    );
  }

  const props = {
    databaseId,
    databaseRootDocId,
    databaseStorageVersion: referenceProps.databaseStorageVersion,
    ...(model.props.title?.toString()
      ? {}
      : { title: titleTextFromString(input.title ?? 'Untitled Database') }),
    // Canvas host blocks participate in edgeless layer ordering; a missing
    // fractional index poisons the layer comparator for the whole doc.
    ...(isCanvasHostParent(model.parent) && !model.props.index
      ? { index: 'a0' }
      : {}),
  };
  updateModelPropsIfChanged(model, props);
}

export function isCanvasHostParent(parent: BlockModel | null | undefined) {
  return parent?.role === 'root' || parent?.flavour === 'affine:surface';
}

export function assertCanvasHostHasXYWH(model: DatabaseBlockModel) {
  if (!isCanvasHostParent(model.parent)) {
    return;
  }

  if (isSerializedXYWH(model.props.xywh)) {
    return;
  }

  throw new BlockSuiteError(
    ErrorCode.DatabaseBlockError,
    `Canvas database host block "${model.id}" is missing xywh.`
  );
}

export function createShardedDatabaseReferenceForHostDoc(
  hostDoc: Store,
  input: ShardedDatabaseRootCreationInput = {}
): ShardedDatabaseReferenceProps {
  const databaseId = input.databaseId ?? hostDoc.workspace.idGenerator();
  const referenceProps = createDatabaseReferenceProps({
    databaseId,
    workspaceId: hostDoc.workspace.id,
  });
  const databaseRootDocId = referenceProps.databaseRootDocId;
  if (!databaseRootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database "${databaseId}" has no root document id.`
    );
  }
  return { id: databaseId, ...referenceProps };
}

export function ensureShardedDatabaseRootForHostDoc(
  hostDoc: Store,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title'> = {}
): ShardedDatabaseReferenceProps {
  ensureShardedDatabaseRootModelForHostDoc(hostDoc, referenceProps, input);
  return referenceProps;
}

export function ensureShardedDatabaseRootModelForHostDoc(
  hostDoc: Store,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title'> = {}
): DatabaseBlockModel {
  const databaseId = referenceProps.databaseId;
  const databaseRootDocId = referenceProps.databaseRootDocId;
  if (!databaseId || !databaseRootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database root creation requires databaseId and databaseRootDocId.'
    );
  }

  const rootDoc = getSystemWorkspaceStore(hostDoc, databaseRootDocId);

  const existingModel = rootDoc.getBlock(databaseId)?.model;
  if (existingModel && existingModel.flavour !== 'affine:database') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database root document "${databaseRootDocId}" already contains non-database block "${databaseId}".`
    );
  }
  if (!existingModel) {
    const createRootDatabaseBlock = () => {
      let rootBlockId = rootDoc.root?.id;
      if (!rootBlockId) {
        rootBlockId = rootDoc.addBlock('affine:page', {
          title: new Text(''),
        });
      }

      rootDoc.addBlock(
        'affine:database',
        {
          id: databaseId,
          ...createShardedDatabaseIdentityProps({
            databaseId,
            databaseRootDocId,
            workspaceId: hostDoc.workspace.id,
          }),
          databaseShardManifest: createDatabaseShardManifest({
            databaseId,
            databaseRootDocId,
            workspaceId: hostDoc.workspace.id,
            shards: [],
          }),
          cells: Object.create(null),
          columns: [],
          canvasPresentation: createCanvasSummaryPresentationState(),
          title: titleTextFromString(input.title ?? 'Untitled Database'),
          views: [],
        },
        rootBlockId
      );
    };

    const rootDocTransact = (rootDoc as { transact?: (fn: () => void) => void })
      .transact;
    if (typeof rootDocTransact === 'function') {
      rootDocTransact.call(rootDoc, createRootDatabaseBlock);
    } else {
      createRootDatabaseBlock();
    }
  } else {
    ensureShardedDatabaseRootModelProps(
      existingModel as DatabaseBlockModel,
      referenceProps,
      input
    );
  }

  const storageModel = rootDoc.getBlock(databaseId)?.model as
    | DatabaseBlockModel
    | undefined;
  if (!storageModel) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database root document "${databaseRootDocId}" did not materialize database "${databaseId}".`
    );
  }
  return storageModel;
}

export function provisionShardedDatabaseHostBlock(
  hostDoc: Store,
  input: ShardedDatabaseHostBlockProvisionInput
): ShardedDatabaseHostBlockProvisionResult {
  const parentId = input.parentId ?? hostDoc.root?.id;
  if (!parentId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database host creation requires a parent block id.'
    );
  }

  const referenceProps = createShardedDatabaseReferenceForHostDoc(hostDoc, {
    databaseId: input.databaseId,
    title: input.title,
  });
  const addBlock =
    input.addBlock ??
    ((flavour, props, parentId) =>
      hostDoc.addBlock(flavour, props as never, parentId));
  const existingHostBlock = hostDoc.getBlock(referenceProps.id)?.model;
  if (existingHostBlock && existingHostBlock.flavour !== 'affine:database') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${referenceProps.id}" is not a database block.`
    );
  }
  if (!existingHostBlock && hostDoc.doc.yBlocks.has(referenceProps.id)) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${referenceProps.id}" exists but is not materialized.`
    );
  }
  if (
    !existingHostBlock &&
    isCanvasHostParent(hostDoc.getBlock(parentId)?.model) &&
    !isSerializedXYWH(input.props?.xywh)
  ) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Canvas database host block "${referenceProps.id}" is missing xywh.`
    );
  }

  const databaseBlockId =
    existingHostBlock?.id ??
    addBlock(
      'affine:database',
      {
        ...referenceProps,
        title: titleTextFromString(input.title),
        ...(input.props ?? {}),
      },
      parentId
    );
  if (!databaseBlockId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database host creation returned an empty block id.'
    );
  }

  const model = hostDoc.getBlock(databaseBlockId)?.model as
    | DatabaseBlockModel
    | undefined;
  if (!model) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${databaseBlockId}" was not materialized.`
    );
  }

  ensureShardedDatabaseHostModelProps(model, referenceProps, {
    title: input.title,
  });
  assertCanvasHostHasXYWH(model);
  const storageModel = ensureShardedDatabaseRootModelForHostDoc(
    hostDoc,
    referenceProps,
    {
      title: input.title,
    }
  );
  return {
    databaseBlockId,
    referenceProps,
    model,
    storageModel,
  };
}
